/* eslint-disable no-restricted-imports */
import moment from "moment";
import React, { useState, useEffect } from "react";
import { ProgressBar } from "react-bootstrap";
import { CreateContactOffCanvas } from "../../../../app/modules/ContactDeals/canvas/CreateContactOffCanvas";
import { CustomerDetailCard } from "../../../../app/modules/Order/NewOrder/components/CustomerDetailCard";
import { useIntl } from "react-intl";
import { initIds } from "../../controls/RightPanel";
import { FormControl, MenuItem, Select } from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DamageWaiverCanva } from "../../../../app/modules/Order/order-canva/DamageWaiverCanva";
import { fulfillmentStatus } from "../../../../app/utils/commonFunction";
import { canvaCancel } from "../../../../app/utils/canvaCloseFunction";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { fetchVersion, fetchMyOrderDetailCard } from "../../../../app/modules/Order/_redux/order/orderAction";
import { Modal } from "react-bootstrap";
import { NameCheckBadge, NameCheckPanel } from "./NameCheck";
import { ManualKycActions } from "./ManualKycActions";
import downloadExport from "../../../../app/utils/downloadExport";

/**
 * KYC status and contract block rendered under the order header.
 *
 * Shows the current verification status chip, opens the contract/ID modal,
 * and — when the verification payload comes back as an error — renders a
 * Retry card that re-fetches the order details from the backend.
 *
 * @param {Object} props
 * @param {Object} [props.cDetails={}] - Contract details (order_id, kyc_status, stripe_check_status, is_kyc_verified).
 * @param {Object} [props.kyc_verification] - Backend verification payload (document, downloaded_files, status, error).
 * @param {Object|null} [props.nameCheck] - ID name check (status, both names, reviewer, can_review); null when it never ran.
 * @returns {JSX.Element}
 */
const RenderKycStatusBlock = ({ cDetails = {}, kyc_verification, nameCheck = null }) => {
  const dispatch = useDispatch();
  const {
    kyc_status: status,
    stripe_check_status,
    is_kyc_verified
  } = cDetails || {};

  const [showContractModal, setShowContractModal] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  /** Re-fetch order details to retry loading ID verification data from Stripe. */
  const handleRetryKyc = () => {
    if (!cDetails?.order_id || retrying) return;
    setRetrying(true);
    dispatch(fetchMyOrderDetailCard(cDetails.order_id)).finally(() =>
      setRetrying(false)
    );
  };

  // ⭐ YOUR EXACT STATUS MAP
  const statusMap = {
    pending_kyc: {
      label: "Pending ID Verification",
      color: "#FF9800",
      bg: "#FFF3E0",
    },
    pending_manual_kyc: {
      label: "Pending Manual ID Verification",
      color: "#FBC02D",
      bg: "#FFF8E1",
    },
    send_pending_kyc: {
      label: "KYC Email Sent",
      color: "#FF9800",
      bg: "#FFF3E0",
    },

    pending_contract_sign: {
      label: "Pending Contract Sign",
      color: "#FF9800",
      bg: "#FFF3E0",
    },
    pending_manual_contract_sign: {
      label: "Pending Manual Contract Sign",
      color: "#FBC02D",
      bg: "#FFF8E1",
    },
    manual_contract_sign_complete: {
      label: "Contract Signed (Manual)",
      color: "#2E7D32",
      bg: "#E8F5E9",
    },

    pending_access: {
      label: "Pending Access",
      color: "#FF9800",
      bg: "#E8F5E9",
    },
    manual_access_complete: {
      label: "Manual Access Complete",
      color: "#2E7D32",
      bg: "#E8F5E9",
    },

    complete: {
      label: "Complete",
      color: "#1B5E20",
      bg: "#D1E7DD",
    },

   void_1_hour_before_kyc: {
    label: "CANCEL (1 Hour Before ID Verification)",
    color: "#D32F2F",
    bg: "#FFEBEE",
  },

   void_1_hour_before_sign: {
    label: "CANCEL (1 Hour Before Contract Sign)",
    color: "#D32F2F",
    bg: "#FFEBEE",
  },

    verified: {
      label: "Verified",
      color: "#2E7D32",
      bg: "#E8F5E9",
    },

    rejected: {
      label: "Rejected",
      color: "#D32F2F",
      bg: "#FFEBEE",
    },

    send_pending_contract_sign: {
      label: "Contract Email Sent",
      color: "#FF9800",
      bg: "#FFF3E0",
    },
  };

  // ⭐ Your stripe color set
  const stripeStatusMap = {
    verified: { color: "#198754", bg: "#E8F5E9" },
    success: { color: "#198754", bg: "#E8F5E9" },
    pending: { color: "#FF8C00", bg: "#FFF4E5" },
    failed: { color: "#DC3545", bg: "#FDECEA" },
    error: { color: "#DC3545", bg: "#FDECEA" },
    failed_verification: { color: "#DC3545", bg: "#FDECEA" },
  };

  const info = statusMap[status] || {
    label: status?.replace(/_/g, " ") || "-",
    color: "#6C757D",
    bg: "#F8F9FA",
  };

  const stripeInfo = stripeStatusMap[stripe_check_status] || null;

  /** Open the ID verification and contract dialog, once the ID is verified. */
  const handleClick = () => { if (is_kyc_verified) setShowContractModal(true);};

  /**
   * Download the order's signed contract.
   *
   * Fetched through axios so the request carries the sign-in token: the
   * endpoint is staff-only. It used to be a plain link to a public address
   * built from the file's storage path.
   *
   * A failure shows contract wording rather than downloadExport's own
   * messages, which are written for report exports. The server answers 404
   * for a missing file, an order outside the user's franchise and a user
   * without a staff role alike, so one message covers them all.
   *
   * @returns {void}
   */
  const downloadPdf = () => {
    if (!cDetails?.file_path || !cDetails?.order_id) return;

    setDownloadError("");
    downloadExport(
      `${import.meta.env.REACT_APP_SITE_URL}order/${cDetails.order_id}/contract/download`,
      () =>
        setDownloadError(
          "The contract could not be downloaded. If this keeps happening, please report it with the order number."
        )
    );
  };

  /**
   * Close the ID verification and contract dialog, clearing any download
   * error so it is not shown again the next time the dialog opens.
   *
   * @returns {void}
   */
  const closeContractModal = () => {
    setShowContractModal(false);
    setDownloadError("");
  };

  /**
   * Stripe returns short tokens ('jpg' | 'png' | 'pdf') in `File.type`.
   * Map them to browser-valid MIME types and file extensions.
   *
   * @param {string} short - Short mime token from the backend.
   * @returns {{type: string, ext: string}}
   */
  const resolveMime = (short) => {
    const map = {
      jpg:  { type: 'image/jpeg',      ext: 'jpg' },
      jpeg: { type: 'image/jpeg',      ext: 'jpg' },
      png:  { type: 'image/png',       ext: 'png' },
      pdf:  { type: 'application/pdf', ext: 'pdf' },
    };
    return map[(short || '').toLowerCase()] || { type: 'application/octet-stream', ext: 'bin' };
  };

  /** Open a base64-encoded file in a new tab using a resolved MIME type. */
  const viewBase64File = (base64, mime) => {
    const { type } = resolveMime(mime);
    const win = window.open();
    if (!win) return;
    win.document.write(
      `<iframe src="data:${type};base64,${base64}" frameborder="0"
      style="width:100%;height:100%"></iframe>`
    );
  };

  const insightColor = (value) => {
    switch (value) {
      case 'lowest':
      case 'low':
      case 'normal':
        return { backgroundColor: '#E8F5E9', color: '#2E7D32' };
      case 'medium':
        return { backgroundColor: '#FFF8E1', color: '#F9A825' };
      case 'high':
      case 'highest':
        return { backgroundColor: '#FFEBEE', color: '#D32F2F' };
      default:
        return { backgroundColor: '#ECEFF1', color: '#455A64' };
    }
  };


  /** Download a base64-encoded file using resolved MIME type and extension. */
  const downloadBase64File = (base64, mime, filename) => {
    const { type, ext } = resolveMime(mime);
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], { type });

    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = `${filename}.${ext}`;
    link.click();
    URL.revokeObjectURL(objectUrl);
  };

  return (
    <>
      <div
        onClick={handleClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          cursor: is_kyc_verified ? "pointer" : "default",
        }}
      >
        <div className="d-flex flex-column gap-1">
          <div
            className="d-inline-flex align-items-center px-3 py-1 rounded-pill"
            style={{
              color: info?.color,
              background: info.bg,
              border: `1px solid ${info?.color}`,
              fontSize: 14,
              fontWeight: 600,
              gap: 8,
              minWidth: 150,
            }}
          >
            <span>{info.label}</span>

            {stripe_check_status && (
              <small
                className="ms-2 px-2 py-0 rounded-pill"
                style={{
                  color: stripeInfo?.color,
                  background: stripeInfo?.bg,
                  border: `1px solid ${stripeInfo?.color}`,
                  fontSize: 12, // smaller than main text
                  lineHeight: "14px",
                }}
              >
                {String(stripe_check_status).toUpperCase()}
              </small>
            )}
          </div>
          <NameCheckBadge status={nameCheck?.status} />
        </div>
      </div>

      {/* MODAL */}
      <Modal
        show={showContractModal}
        centered
        size="lg"
        onHide={closeContractModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>ID Verification & Contract</Modal.Title>
        </Modal.Header>

       <Modal.Body className="px-1 py-1">

        {/* CONTRACT SECTION */}
        {cDetails?.file_path && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-semibold">Contract</h6>
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={downloadPdf}
              >
              Download
              </button>
            </div>
            {downloadError && (
              <div className="text-danger px-4 pb-3">{downloadError}</div>
            )}
          </div>
        )}

        <hr/>
        <NameCheckPanel nameCheck={nameCheck} orderId={cDetails?.order_id} />
        {/* KYC ERROR STATE — transient failure, user can retry */}
        {!kyc_verification?.document && kyc_verification?.status === 'error' && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-center py-4">
              <h6 className="fw-semibold mb-2">ID Verification</h6>
              <p className="text-muted mb-3">
                {kyc_verification.error || 'Unable to load verification data.'}
              </p>
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={handleRetryKyc}
                disabled={retrying}
              >
                {retrying ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          </div>
        )}
        {/* KYC MANUAL STATE — verified manually, no Stripe document to show */}
        {!kyc_verification?.document
          && kyc_verification?.status !== 'error'
          && kyc_verification?.kyc_method === 'manual' && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-center py-4">
              <h6 className="fw-semibold mb-2">ID Verification</h6>
              <p className="text-muted mb-0">Manually verified — no Stripe document on file.</p>
            </div>
          </div>
        )}
        {/* KYC PENDING STATE — Stripe session started, awaiting customer completion */}
        {!kyc_verification?.document
          && kyc_verification?.status !== 'error'
          && kyc_verification?.kyc_method !== 'manual'
          && ['requires_input', 'processing', 'pending'].includes(kyc_verification?.status) && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-center py-4">
              <h6 className="fw-semibold mb-2">Verification link sent</h6>
              <p className="text-muted mb-0">
                Awaiting customer ID verification at Stripe.
                {cDetails?.kyc_requested_at && (
                  <>
                    {' '}Requested{' '}
                    {moment(cDetails.kyc_requested_at).format('DD-MM-YYYY HH:mm')}.
                  </>
                )}
              </p>
            </div>
          </div>
        )}
        {/* KYC UNAVAILABLE STATE — unexpected status, no document available */}
        {!kyc_verification?.document
          && kyc_verification?.status !== 'error'
          && kyc_verification?.kyc_method !== 'manual'
          && !['requires_input', 'processing', 'pending'].includes(kyc_verification?.status) && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-center py-4">
              <h6 className="fw-semibold mb-2">ID Verification</h6>
              <p className="text-muted mb-0">ID verification data is not available for this order.</p>
            </div>
          </div>
        )}
        {/* KYC SECTION */}
        {kyc_verification?.document && (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="mb-0 fw-semibold">ID Verification </h6>
              {/* STATUS */}
              <div className="d-flex align-items-center mb-2">
                <span className="me-2 fw-semibold">Status:</span>
                <span
                  className='py-2 px-3 rounded-pill'
                  style={ kyc_verification.document.status === 'verified'? {} :{color: "#D32F2F",backgroundColor: "#FFEBEE"}}
                >
                  {kyc_verification.document.status}
                </span>
              </div>

              {/* ERROR */}
              {kyc_verification.document.error?.reason && (
                <div className="py-2 px-3 mb-3" style={ kyc_verification.document.status === 'verified'? {} :{color: "#D32F2F",backgroundColor: "#FFEBEE"}}>
                  {kyc_verification.document.error.reason}
                </div>
              )}

              {/* DETAILS GRID */}
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <strong>Document Type:</strong>
                  <div>{kyc_verification.document.type}</div>
                </div>

                <div className="col-md-6">
                  <strong>Issued Country:</strong>
                  <div>{kyc_verification.document.issuing_country}</div>
                </div>

                <div className="col-md-6">
                  <strong>Name:</strong>
                  <div>
                    {kyc_verification.document.first_name}{' '}
                    {kyc_verification.document.last_name}
                  </div>
                </div>

                <div className="col-md-6">
                  <strong>Issued Date:</strong>
                  <div>
                    {kyc_verification.document.issued_date.day}-
                    {kyc_verification.document.issued_date.month}-
                    {kyc_verification.document.issued_date.year}
                  </div>
                </div>

                <div className="col-12">
                  <strong>Address:</strong>
                  <div className="text-muted">
                    {[
                      kyc_verification.document.address.line1,
                      kyc_verification.document.address.city,
                      kyc_verification.document.address.state,
                      kyc_verification.document.address.postal_code,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
              </div>

              {/* FILES */}
              <div>
                <strong className="d-block mb-2">Files</strong>

                {kyc_verification.downloaded_files?.map((file, index) =>
                  file?.data ? (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center border rounded px-3 py-2 mb-2"
                    >
                      <span>
                        {index === 0
                          ? 'Document Front'
                          : 'Document Back'}
                      </span>

                      <div>
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() =>
                            viewBase64File(file.data, file.mime)
                          }
                        >
                          👁 View
                        </button>

                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() =>
                            downloadBase64File(
                              file.data,
                              file.mime,
                              index === 0
                                ? 'document_front'
                                : 'document_back'
                            )
                          }
                        >
                        Download
                        </button>
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
        )}
        {/* INSIGHTS SECTION */}
          {kyc_verification?.insights && (
            <div className="mb-4">
              <h6 className="fw-semibold mb-3">Verification Insights</h6>

              {/* DOCUMENT INSIGHTS */}
              {kyc_verification.insights.document && (
                <div className="mb-3">
                  <strong className="d-block mb-2">Document Checks</strong>

                  {Object.entries(kyc_verification.insights.document).map(
                    ([key, insight]) => (
                      <div
                        key={key}
                        className="d-flex justify-content-between align-items-center border rounded px-3 py-2 mb-2"
                      >
                        <div className="text-capitalize">
                          {key.replaceAll('_', ' ')}
                          <div className="text-muted small">
                            {insight.message}
                          </div>
                        </div>

                        <span
                          className="px-3 py-1 rounded-pill text-uppercase small"
                          style={insightColor(insight.value)}
                        >
                          {insight.value}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}

              {/* BEHAVIOR INSIGHTS */}
              {kyc_verification.insights.behavior && (
                <div className="mb-3">
                  <strong className="d-block mb-2">Behavior</strong>

                  {Object.entries(kyc_verification.insights.behavior).map(
                    ([key, insight]) => (
                      <div
                        key={key}
                        className="d-flex justify-content-between align-items-center border rounded px-3 py-2 mb-2"
                      >
                        <div className="text-capitalize">
                          {key.replaceAll('_', ' ')}
                          <div className="text-muted small">
                            {insight.message}
                          </div>
                        </div>

                        <span
                          className="px-3 py-1 rounded-pill text-uppercase small"
                          style={insightColor(insight.value)}
                        >
                          {insight.value}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}

      </Modal.Body>


        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={closeContractModal}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};


/**
 * Renders the order detail panel showing customer, address, products, and operational actions.
 *
 * @param {Object} props
 * @param {Object} props.orderDetail - Order data including customer details, products, addresses, and status used to populate the panel.
 * @param {Object} props.location - React Router location object used to derive routing/navigation context within the panel.
 * @returns {JSX.Element} The rendered order info panel.
 */
export function OrderInfo({ orderDetail, location }) {
  const dispatch = useDispatch();

  const init = ["kt_quick_panelone"];

  useEffect(() => {
    initIds(init);
  }, [init]);
  const { role } = useSelector(
    (state) => ({
      role: state.auth.user.roles,
    }),
    shallowEqual
  );
  const intl = useIntl();
  const [openCanva, setOpenCanva] = useState(false);
  const [openDamageCanva, setOpenDamageCanva] = useState(false);
  let cus_address = orderDetail?.customer_details?.address || "";
  if (cus_address !== "") {
    cus_address = cus_address?.replaceAll(", ", ",");
  }
  const editValues = {
    first_name: orderDetail?.customer_details?.first_name || "",
    last_name: orderDetail?.customer_details?.last_name || "",
    email: orderDetail?.customer_details?.contact?.email || [],
    phone: orderDetail?.customer_details?.contact?.phone || [],
    postcode: orderDetail?.customer_details?.postcode || "",
    city: orderDetail?.customer_details?.city || "",
    address: cus_address,
    user_id: orderDetail?.customer_details?.contact?.user_id || "",
    is_address_manual:
      orderDetail?.customer_details?.contact?.is_address_manual || 0,
    franchise_id: orderDetail?.franchise_id || "",
    contact_owner_id:
      orderDetail?.customer_details?.contact?.contact_owner_id || "",
    contact_order_id: orderDetail?.id || "",
  };
  /**
   * Act on the order's actions menu: open the T&Cs, the addendum or the app
   * sign-in page in a new tab, or open the damage panel for anything else.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   * @returns {void}
   */
  const handleSelect = (e) => {
    if (e.target.value === "T&Cs") {
      const newTab = window.open(
        `/terms-and-condition?franchise_id=${orderDetail?.franchise_id ||
          ""}&order_id=${orderDetail?.id}&version=${orderDetail?.version}`,
        "_blank"
      );
      newTab.focus();
      // navigate({
      //   pathname: `/terms-and-condition`,
      //   state: {
      //     franchise_id: orderDetail?.franchise_id || "",
      //     order_id: orderDetail?.id || "",
      //   },
      // });
    }
    else if(e.target.value === "addendum-order") {
      const newTab = window.open(
        `/addendum-order?franchise_id=${orderDetail?.franchise_id ||
          ""}&order_id=${orderDetail?.id}`,
        "_blank"
      );
      newTab.focus();
    } else if (e.target.value === "app-signin") {
      const newTab = window.open(`/app-signin/${orderDetail?.id}`, "_blank");
      newTab?.focus();
    } else {
      setOpenDamageCanva(true);
    }
  };

  /**
   * The plan's display name: "Collect & Store" or "Drop & Store" for those
   * two product ids, otherwise the value as given.
   *
   * @param {string} plan The plan type from the order.
   * @returns {string}
   */
  const formatPlanType = (plan) => {
    if (!plan) return "";

    switch (plan.toLowerCase()) {
      case import.meta.env.REACT_APP_PRODUCT_Collect_Store:
        return "Collect & Store";
      case import.meta.env.REACT_APP_PRODUCT_Drop_Store:
        return "Drop & Store";
      default:
        return plan;
    }
  };

  useEffect(() => {
    if (
      location?.search === "?search" &&
      orderDetail?.customer_details?.first_name
    ) {
      canvaCancel("kt_StorageDetail_panel_toggle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, orderDetail?.customer_details?.first_name]);

  return (
    <>
      <div className="card card-custom gutter-b">
        <div className="card-body ">
          <div className="d-flex justify-content-between">
            <div>
              <h5 className="card-title align-items-start flex-column">
                <span className="font-weight-bolder text-dark">
                  {intl.formatMessage({ id: "Order.Number" })} :{" "}
                  {orderDetail?.booking_reference || ""}{" "}
                  <span
                    className="font-weight-bolder text-dark"
                    style={{ paddingTop: "6px" }}
                  >
                    <sup
                      // style={{
                      //   color:
                      //     orderDetail?.is_collection_completed === 1
                      //       ? "green"
                      //       : "red",
                      // }}
                      className="top-4"
                    >
                      <span
                        class={`badge badge-pill ${
                          orderDetail?.order_details?.pod_plan_type ===
                          "First Class"
                            ? "badge-gold"
                            : orderDetail?.order_details?.pod_plan_type ===
                              "Economy"
                            ? "badge-bronze"
                            : orderDetail?.order_details?.pod_plan_type ===
                              "Premium"
                            ? "badge-silver"
                            : ""
                        }`}
                        style={{ fontSize: "13px" }}
                      >
                        <strong>
                          {orderDetail?.order_details?.pod_plan_type !==
                          "Unknown Product"
                            ? formatPlanType(orderDetail?.order_details?.pod_plan_type)
                            : ""}
                        </strong>
                      </span>
                    </sup>
                  </span>
                </span>
                <span className="d-block text-muted pt-2 font-size-sm font-weight-normal">
                  {intl.formatMessage({ id: "Order.Date" })} :{" "}
                  {moment(orderDetail?.created_at || "").format("DD-MM-yyyy") ||
                    ""}
                </span>
                {role &&
                role?.length &&
                role[0] === "super-admin" &&
                (orderDetail?.subscription_id?.includes("sub") ||
                  orderDetail?.subscription_scheduled_id) ? (
                  <span className="d-block text-muted pt-2 font-size-sm font-weight-normal">
                    Stripe Subscription Id :{" "}
                    {orderDetail?.subscription_id?.includes("sub")
                      ? orderDetail?.subscription_id
                      : orderDetail?.subscription_scheduled_id || ""}
                  </span>
                ) : (
                  ""
                )}
              </h5>
                {orderDetail?.is_container == 1 && orderDetail?.contract && (
                  <RenderKycStatusBlock
                    cDetails={orderDetail.contract}
                    kyc_verification={orderDetail?.kyc_verification}
                    nameCheck={orderDetail?.name_check}
                  />
                )}
                {orderDetail?.is_container == 1 && orderDetail?.contract && (
                  // Keyed by order, so its uploaded files and any open
                  // dialog never carry over to a different order.
                  <ManualKycActions
                    key={orderDetail?.id}
                    orderDetail={orderDetail}
                  />
                )}
            </div>

            <div className="storage-process">
              <div className="mb-2 d-flex justify-content-between">
                <div className="orderActionSelect">
                  <FormControl fullWidth>
                    <Select
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                      className="form-control"
                      IconComponent={(props) => (
                        <i {...props}>
                          <KeyboardArrowDownIcon />
                        </i>
                      )}
                      inputProps={{ "aria-label": "Without label" }}
                      displayEmpty
                      onChange={(e) => handleSelect(e)}
                      value={""}
                      // onBlur={handleBlur}
                      // value={values.search_view}
                    >
                      <MenuItem value="">Action</MenuItem>
                      <MenuItem value="T&Cs">T&Cs</MenuItem>
                      <MenuItem value="Damage/Waiver">Damage/Waiver</MenuItem>
                      <MenuItem value="addendum-order">Order Addendum</MenuItem>
                      {role && role?.length && role[0] === "super-admin" ? (
                        <MenuItem value="app-signin">
                          App Sign-in as Customer
                        </MenuItem>
                      ) : null}
                    </Select>
                  </FormControl>
                </div>
                <button
                  className="btn btn-primary font-weight-bolder font-size-sm btn-sm"
                  id="kt_StorageDetail_panel_toggle"
                  onClick={() => {
                    if (orderDetail.id) {
                      dispatch(
                        fetchVersion(orderDetail.franchise_id, orderDetail.id)
                      );
                    }
                  }}
                >
                  {intl.formatMessage({ id: "Storage.Details" })}
                </button>
              </div>
              <div className="row align-items-center">
                <div className="col-4">
                  {orderDetail?.is_cancel === 1
                    ? "Cancelled"
                    : intl.formatMessage({ id: "Fulfillment" })}
                </div>
                <div className="col-8">
                  {orderDetail?.is_container == "1" ? (
                    <ProgressBar
                      variant={
                        orderDetail?.is_cancel === 1 ? "danger" : "success"
                      }
                      now={
                        orderDetail?.container_status == "1"
                          ? 20
                          : orderDetail?.container_status == "2"
                          ? 40
                          : orderDetail?.container_status == "3"
                          ? 60
                          : orderDetail?.container_status == "4"
                          ? 80
                          : orderDetail?.container_status == "5"
                          ? 100
                          : 0
                      }
                      label={
                        orderDetail?.container_status == "1"
                          ? "Res"
                          : orderDetail?.container_status == "2"
                          ? "Confirmed"
                          : orderDetail?.container_status == "3"
                          ? "In Storage"
                          : orderDetail?.container_status == "4"
                          ? "Completed"
                          : orderDetail?.container_status == "5"
                          ? "Cancelled"
                          : "-"
                      }
                    />
                  ) : (
                    <ProgressBar
                      variant={
                        orderDetail?.is_cancel === 1 ? "danger" : "success"
                      }
                      now={
                        fulfillmentStatus(orderDetail?.fulfillment_stage)
                          ?.now || 0
                      }
                      label={
                        fulfillmentStatus(orderDetail?.fulfillment_stage)
                          ?.title || ""
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="separator separator-solid my-5"></div>
          <CustomerDetailCard
            detailData={orderDetail?.customer_details || {}}
            franchise={orderDetail?.franchise_details?.name || ""}
            agent={orderDetail?.agent_details?.first_name || ""}
            agentLast={orderDetail?.agent_details?.last_name || ""}
            orderDetail={orderDetail}
            setOpenCanva={setOpenCanva}
            type={"order"}
          />
        </div>
      </div>
      {openCanva ? (
        <>
          <CreateContactOffCanvas
            title={"Customer Details"}
            type={orderDetail?.customer_details ? "edit" : ""}
            check={"customer"}
            checked={"customers"}
            editCustomer={editValues}
            editCustomerId={orderDetail?.customer_details?.contact?.id || ""}
            setOpenCanva={setOpenCanva}
            orderId={orderDetail?.id || ""}
          />
          <div
            className="offcanvas-overlay"
            onClick={() => setOpenCanva(false)}
          ></div>
        </>
      ) : (
        ""
      )}
      {openDamageCanva && (
        <>
          <DamageWaiverCanva
            setOpenDamageCanva={setOpenDamageCanva}
            orderDetail={orderDetail}
          />
          <div
            className="offcanvas-overlay"
            onClick={() => setOpenDamageCanva(false)}
          ></div>
        </>
      )}
    </>
  );
}
